import type { SATTestAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import { makeQueryModelV2, type QueryOrder, queryLimitToSQL, queryOrderToSQL } from './common'
import SeriesQ from './SeriesQ'

const SATTestQ = {
  ...makeQueryModelV2<SATTestAttributes>('sat_test')([
    'id',
    'title',
    'test_date',
    'series_creation_deadline',
    'series_creation_deadline',
    'learner_registration_deadline',
    'onboarding_session_deadline',
    'series_start_date_min',
    'series_end_date_max',
    'learner_released_at',
    'tutor_released_at',
    'onboarding_session_released_at',
    'created_at',
    'deleted_at',
    'updated_at',
  ]),

  where: (
    {
      id,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SATTestAttributes> => SQL`
        FROM ${SATTestQ}
        WHERE
            ${id != null ? SQL`${SATTestQ.id} = ${id} AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  getTitleForSATSeries: (): SQLFragment<string | null> =>
    SQL`(SELECT ${SATTestQ.title} ${SATTestQ.where({}, SQL`${SATTestQ.id} = ${SeriesQ.getSATTestId()}`)})`,

  learnerRegistrationHasNotPassed: (): SQLFragment<boolean> =>
    SQL`${SATTestQ.learner_registration_deadline} > NOW()`,

  seriesCreationDeadlineHasNotPassed: (): SQLFragment<boolean> =>
    SQL`${SATTestQ.series_creation_deadline} > NOW()`,

  hasNotEnded: (): SQLFragment<boolean> => SQL`${SATTestQ.series_end_date_max} > NOW()`,

  hasBeenReleasedToLearners: (): SQLFragment<boolean> =>
    SQL`${SATTestQ.learner_released_at} <= NOW()`,

  hasBeenReleasedToOnboardingHosts: (): SQLFragment<boolean> =>
    SQL`${SATTestQ.onboarding_session_released_at} <= NOW()`,

  hasBeenReleasedToTutors: (): SQLFragment<boolean> => SQL`${SATTestQ.tutor_released_at} <= NOW()`,

  learnersCanRegister: (): SQLFragment<boolean> =>
    SQL`${SATTestQ.hasBeenReleasedToLearners()} AND ${SATTestQ.learnerRegistrationHasNotPassed()}`,

  tutorsCanCreateSeries: (): SQLFragment<boolean> =>
    SQL`${SATTestQ.hasBeenReleasedToTutors()} AND ${SATTestQ.seriesCreationDeadlineHasNotPassed()}`,

  getNextTestOpenForLearnerRegistration: ({
    excludeCurrentlyOpen = false,
  }: {
    excludeCurrentlyOpen?: boolean
  }): SQLFragment<SATTestAttributes> =>
    SATTestQ.where(
      { order: [[SATTestQ.test_date, 'asc']], limit: 1 },
      SQL`${SATTestQ.learnerRegistrationHasNotPassed()} AND ${excludeCurrentlyOpen ? SQL`NOT ${SATTestQ.hasBeenReleasedToLearners()}` : SQL`TRUE`}`
    ),

  getNextTestOpenForTutorRegistration: ({
    excludeCurrentlyOpen = false,
  }: {
    excludeCurrentlyOpen?: boolean
  }): SQLFragment<SATTestAttributes> =>
    SATTestQ.where(
      { order: [[SATTestQ.test_date, 'asc']], limit: 1 },
      SQL`${SATTestQ.seriesCreationDeadlineHasNotPassed()} AND ${excludeCurrentlyOpen ? SQL`NOT ${SATTestQ.hasBeenReleasedToTutors()}` : SQL`TRUE`}`
    ),

  getSATTestOpenForTutorRegistration: (): SQLFragment<SATTestAttributes> =>
    SATTestQ.where(
      { order: [[SATTestQ.test_date, 'asc']], limit: 1 },
      SQL`${SATTestQ.hasBeenReleasedToTutors()} AND ${SATTestQ.seriesCreationDeadlineHasNotPassed()}`
    ),

  getSATTestOpenForLearnerRegistration: (): SQLFragment<SATTestAttributes> =>
    SATTestQ.where(
      { order: [[SATTestQ.test_date, 'asc']], limit: 1 },
      SQL`${SATTestQ.hasBeenReleasedToLearners()} AND ${SATTestQ.learnerRegistrationHasNotPassed()}`
    ),

  getCurrentSATTest: (): SQLFragment<SATTestAttributes> =>
    SATTestQ.where(
      { order: [[SATTestQ.test_date, 'asc']], limit: 1 },
      SQL`${SATTestQ.series_start_date_min} <= NOW() AND ${SATTestQ.series_end_date_max} > NOW()`
    ),

  getUpcomingBootcamps: ({
    type,
    limit,
  }: {
    type: 'learner' | 'tutor'
    limit?: number
  }): SQLFragment<SATTestAttributes> =>
    SATTestQ.where(
      { order: [[SATTestQ.test_date, 'asc']], limit },
      type === 'learner'
        ? SATTestQ.learnerRegistrationHasNotPassed()
        : SATTestQ.seriesCreationDeadlineHasNotPassed()
    ),
}

export default SATTestQ
